import React, { useEffect, useState, useContext, useCallback } from "react";
import { GoogleLogin } from "@react-oauth/google";
import HostApi from "../../../../data/settings/HostApi";
import UserImplDto from "../../../../data/dto/impl/UserImplDto";
import ModalsContext from "../../../../domain/providers/modal/ModalsContext";
import UserContext from "../../../../domain/providers/user/UserContext";
import { trackEvent } from "../../../../lib/analytics";
import { API_URL, GOOGLE_CLIENT_ID } from "../../../../config/env";

// `largo`: botón oficial de Google (ancho, con texto); el modo por defecto es el icono redondo.
export const GoogleAuthButton = ({ largo = false }: { largo?: boolean }) => {
    const [isLoading, setIsLoading] = useState(false);
    const { setUser } = useContext(UserContext) as any;
    const { addToast } = useContext(ModalsContext) as any;

    const googleClientId = GOOGLE_CLIENT_ID;

    const processLogin = useCallback(
        async (credential: string) => {
            setIsLoading(true);
            try {
                // El backend verifica el token con Google y saca el email de ahí; también crea la cuenta si no existe.
                const logRes = await fetch(`${API_URL}/login-google/`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ credential }),
                });
                if (!logRes.ok) throw new Error(`login-google ${logRes.status}`);

                const response = await logRes.json();
                HostApi.setToken(response.token);
                setUser(UserImplDto.fromJson(response.user));
                // `inmediato` porque la recarga de abajo descarta la cola de envío.
                // Sin `registro_completado`: desde el cliente no se distingue una cuenta nueva.
                trackEvent("sesion_iniciada", { metodo: "google" }, { inmediato: true });
                window.location.reload();
            } catch (error) {
                console.error("Auth Error:", error);
                addToast("Error al sincronizar con el servidor", "error");
            } finally {
                setIsLoading(false);
            }
        },
        [setUser, addToast],
    );

    const handleManualClick = () => {
        const google = (window as any).google;

        if (google?.accounts?.id) {
            // --- TRUCO PARA DESBLOQUEAR EL MODAL ---
            // Borramos la cookie de estado de Google para que no nos bloquee por 'suppressed_by_user'
            document.cookie = "g_state=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";

            google.accounts.id.cancel(); // Cancelamos cualquier petición pendiente
            setIsLoading(true);

            google.accounts.id.initialize({
                client_id: googleClientId,
                callback: (res: any) => processLogin(res.credential),
                itp_support: true,
                ux_mode: "popup", // El modo popup es más resistente a bloqueos
                use_fedcm_for_prompt: false,
            });

            google.accounts.id.prompt((notification: any) => {
                if (notification.isNotDisplayed() || notification.isDismissedMoment()) {
                    setIsLoading(false);
                    // Si sigue fallando, es probable que Google detecte el spam de clics
                }
            });
        } else {
            addToast("Los servicios de Google aún están cargando.", "info");
        }
    };
    useEffect(() => {
        if (!largo) handleManualClick();
    }, []);

    if (largo) {
        return (
            <div className="d-flex justify-content-center" style={{ opacity: isLoading ? 0.6 : 1 }}>
                <GoogleLogin
                    onSuccess={(res) => res.credential && processLogin(res.credential)}
                    onError={() => addToast("No se pudo iniciar sesión con Google", "error")}
                    text="continue_with"
                    shape="pill"
                    width="320"
                />
            </div>
        );
    }

    return (
        <div className="flex flex-col items-center justify-center" style={{ marginTop: 10 }}>
            <button
                onClick={handleManualClick}
                disabled={isLoading}
                className={`w-12 h-12 rounded-full flex items-center justify-center bg-white transition-all duration-200 ${
                    isLoading ? "cursor-wait opacity-70" : "hover:shadow-md active:scale-95"
                }`}
            >
                <svg width="28" height="28" viewBox="0 0 24 24">
                    {/* ... path del logo de Google ... */}
                    <path
                        fill="#4285F4"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                        fill="#34A853"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                        fill="#FBBC05"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
                    />
                    <path
                        fill="#EA4335"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 12-4.53z"
                    />
                </svg>
            </button>
        </div>
    );
};
